import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { store, useDB } from '../data/store';
import { me, yearCounts } from '../data/queries';
import { stickersFor, stickerYears, type Sticker } from '../lib/stickers';
import { LAST_YEAR, YEARS, yearColor, yearFontStyle, eraOf } from '../lib/years';
import { Icon, Rainbow } from './Bits';
import { useUI } from './ui';

/** Year of the day: the same for everyone on a given day. */
export function yearOfTheDay(now = Date.now()): number {
  const day = Math.floor(now / 86_400_000);
  return YEARS[(day * 7 + 3) % YEARS.length]!;
}

export function Layout() {
  const db = useDB();
  const loc = useLocation();
  const m = /^\/y\/(\d{4})/.exec(loc.pathname);
  const current = m ? Number(m[1]) : undefined;
  useEffect(() => { document.documentElement.dataset.mode = db.settings.mode; }, [db.settings.mode]);
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <Header />
      <YearDial current={current} />
      {db.settings.stickers && <StickerRails year={current ?? yearOfTheDay()} />}
      <div className="wrap">
        <PreviewBanner />
        <div id="main"><Outlet /></div>
      </div>
      <Footer />
    </>
  );
}

function Header() {
  const db = useDB(), ui = useUI();
  const user = me(db);
  const unread = user ? db.notifications.filter(n => n.userId === user.id && !n.read).length : 0;
  const light = db.settings.mode === 'light';
  return (
    <header className="site">
      <div className="wrap bar">
        <Link to="/" className="logo" aria-label="nostalgia. home">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5a7.5 7.5 0 0 0-7.5 7.5v11.5l2.6-2 2.5 2 2.4-2 2.4 2 2.5-2 2.6 2V10A7.5 7.5 0 0 0 12 2.5z" fill="#FFFFFF" stroke="#221E2B" strokeWidth="1.6" strokeLinejoin="round" /><circle cx="9.4" cy="10.4" r="1.3" fill="#221E2B" /><circle cx="14.6" cy="10.4" r="1.3" fill="#221E2B" /></svg>
          <b><Rainbow text="nostalgia" /><i /></b>
        </Link>
        <nav className="nav" aria-label="Main">
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/ideas">Ideas</NavLink>
          <NavLink to="/tags">Hashtags</NavLink>
          <NavLink to="/c">Communities</NavLink>
          <NavLink to="/kindness">Kindness</NavLink>
          <NavLink to="/forgotten">Forgotten Forever?</NavLink>
          <NavLink to="/there">You had to be there</NavLink>
          <NavLink to="/capsule">Time Capsule</NavLink>
        </nav>
        <div className="tools">
          <Link className="ib" to="/search" aria-label="Search"><Icon name="search" /></Link>
          <Link className="ib bell" to="/notifications" aria-label={unread ? `Notifications, ${unread} new` : 'Notifications'}><Icon name="bell" />{unread > 0 && <span className="dot">{unread > 9 ? '9+' : unread}</span>}</Link>
          <button className="ib" onClick={() => store.setSettings({ mode: light ? 'dark' : 'light' })} aria-label={light ? 'Lights off' : 'Lights on'}><Icon name={light ? 'moon' : 'sun'} /></button>
          {user
            ? <Link className="ib me" to={`/u/${user.handle}`} aria-label="Your profile" style={{ color: `var(--rb-${user.color})` }}>{user.handle.charAt(0).toUpperCase()}</Link>
            : <button className="ib" onClick={ui.askProfile} aria-label="Pick a name"><Icon name="user" /></button>}
          <button className="btn hot tmb" onClick={ui.takeMeBack}><Icon name="rewind" /><span>Take me back</span></button>
          <Link className="btn ghost new" to="/new" aria-label="New memory"><Icon name="plus" /><span>Memory</span></Link>
        </div>
      </div>
    </header>
  );
}

function YearDial({ current }: { current?: number }) {
  const db = useDB();
  const counts = useMemo(() => yearCounts(db), [db]);
  const max = Math.max(1, ...counts.values());
  useEffect(() => { document.querySelector('.yr[aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'center' }); }, [current]);
  return (
    <div className="dial">
      <nav className="wrap track" aria-label={`Years 1995 to ${LAST_YEAR}`}>
        {YEARS.map(y => (
          <Link key={y} to={`/y/${y}`} className={'yr' + (y === LAST_YEAR ? ' now' : '')} aria-current={current === y ? 'page' : undefined}
            style={{ ['--yc' as string]: yearColor(y) }} title={`${y}, ${eraOf(y).name} era`}>
            <i style={{ height: 5 + Math.round(((counts.get(y) ?? 0) / max) * 30) }} />
            <b style={db.settings.yearFonts ? yearFontStyle(y) : undefined}>{y}</b>
          </Link>
        ))}
      </nav>
    </div>
  );
}

function StickerRails({ year }: { year: number }) {
  const list = useMemo(() => stickersFor(year, 8), [year]);
  const [open, setOpen] = useState<{ s: Sticker; x: number; y: number } | null>(null);
  useEffect(() => { setOpen(null); }, [year]);
  const place = (i: number) => {
    const r = ((year * 31 + i * 17) % 29) - 14;
    return { top: 160 + Math.floor(i / 2) * 124 + ((i * 13) % 20), [i % 2 ? 'right' : 'left']: 8 + ((i * 11) % 24), ['--r' as string]: `${r}deg`, ['--s' as string]: `${72 + ((i * 7) % 16)}px` };
  };
  return (
    <div className="rails" aria-label={`Stickers from around ${year}`}>
      {list.map((s, i) => (
        <button key={s.id} className="stk" style={place(i)} aria-label={`Sticker: ${s.name}`}
          onClick={e => { const b = e.currentTarget.getBoundingClientRect(); setOpen(open?.s.id === s.id ? null : { s, x: b.left + b.width / 2, y: b.bottom }); }}>
          <svg viewBox="0 0 100 100" aria-hidden="true" dangerouslySetInnerHTML={{ __html: s.svg /* our own static drawings, never user input */ }} />
        </button>
      ))}
      {open && <StickerPop s={open.s} x={open.x} y={open.y} onClose={() => setOpen(null)} />}
    </div>
  );
}

function StickerPop({ s, x, y, onClose }: { s: Sticker; x: number; y: number; onClose: () => void }) {
  const mid = Math.round((s.years[0] + s.years[1]) / 2);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k); }, [onClose]);
  const left = Math.max(12, Math.min(x - 145, window.innerWidth - 302));
  const top = Math.min(y + 8, window.innerHeight - 190);
  return (
    <div className="pop" role="dialog" aria-label={s.name} style={{ left, top }}>
      <button className="ib x" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
      <h4>{s.name}</h4>
      <p className="muted small">{stickerYears(s)}</p>
      <p>Remember it?</p>
      <div className="row">
        <Link className="btn hot sm" to={`/y/${Math.min(LAST_YEAR, mid)}`} onClick={onClose}>See that year</Link>
        <Link className="btn ghost sm" to={`/new?year=${s.years[0]}&title=${encodeURIComponent(s.name)}`} onClick={onClose}>Post about it</Link>
      </div>
    </div>
  );
}

function PreviewBanner() {
  const [hidden, setHidden] = useState(() => { try { return sessionStorage.getItem('nl-banner') === '1'; } catch { return false; } });
  if (hidden) return null;
  return (
    <div className="banner" role="note">
      <span><b>Preview.</b> Accounts and the shared database are not switched on yet, so what you post stays in this browser. Posts marked "Example" are made up to show the design.</span>
      <button className="ib" aria-label="Hide this note" onClick={() => { setHidden(true); try { sessionStorage.setItem('nl-banner', '1'); } catch { /* ignore */ } }}><Icon name="x" /></button>
    </div>
  );
}

function Footer() {
  return (
    <footer className="foot">
      <div className="wrap foot-in">
        <p className="tagline"><Rainbow text="nostalgia will be the death of me." offset={3} /></p>
        <nav aria-label="About Nostalgia">
          <Link to="/about">About</Link><Link to="/help">Help</Link><Link to="/guidelines">Community guidelines</Link>
          <Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/impressum">Impressum</Link><Link to="/settings">Settings</Link>
        </nav>
      </div>
    </footer>
  );
}
